import {
  checkArgument,
  checkExhaustive,
  checkExists,
  checkState,
} from 'external/dev_april_corgi+/js/common/asserts';
import {
  DefaultMap,
  getFirstElement,
  getOnlyElement,
} from 'external/dev_april_corgi+/js/common/collections';

import {approxEqual} from 'external/dev_april_corgi+/js/common/comparisons';

import {ActionsBuffer} from '../execution/actions_dispatcher';
import {PipeId, hashMaterial} from '../execution/graph';
import {REMAINDER_WELL_VOLUME_NL} from '../execution/opentrons';
import {
  PTransformer,
  PipeContext,
  Source,
  walkMaterials,
} from '../execution/pipes';
import {
  ArrayValue,
  ConcentrationValue,
  DataValue,
  MaterialId,
  MaterialValue,
  PlateValue,
  PlatedMaterialValue,
  StringValue,
  TableType,
  TableValue,
  VolumeValue,
} from '../types/data';
import {PlateState, WellState} from '../types/lab';
import {harmonizeUnits} from '../types/units';

export const LABEL_COLUMN_ID = 'Label';
const PREMIX_LABEL = 'premix';
export const MINIMUM_PIPETTE_VOLUME_NL = 1_000;
const MINIMUM_STEP_VOLUME_NL = 1_000;
export const MIXED_TREATMENT = 'mixed';
export const REDUCED_TREATMENT = 'reduced';

export class MixingTransformer extends PTransformer<
  DataValue,
  ArrayValue<PlatedMaterialValue>
> {
  constructor(
    id: PipeId,
    private readonly materials: Array<
      Source<ArrayValue<PlatedMaterialValue> | PlatedMaterialValue>
    >,
    private readonly mixes: Source<TableValue>,
    private readonly plate: Source<PlateValue>,
    private readonly remainder: Source<PlatedMaterialValue>,
  ) {
    super(id, [...materials, mixes, plate, remainder]);
  }

  async collate(context: PipeContext): Promise<void> {
    // TODO(april): there's a bug in here if the same material is provided multiple times. We don't
    // do per-source accounting
    const platedMaterials: PlatedMaterialValue[] = [];
    for (const {value, source, reject} of this.values.values()) {
      if (this.materials.indexOf(source as any) >= 0) {
        walkMaterials(value, reject, v => {
          platedMaterials.push(v);
        });
      }
    }
    platedMaterials.sort((a, b) =>
      a.material.label.localeCompare(b.material.label),
    );
    const mixes = checkExists(this.values.get(this.mixes));
    const mixesTable = checkExists(mixes.value) as TableValue;
    const plate = checkExists(this.values.get(this.plate));
    const plateId = (checkExists(plate.value) as PlateValue).id;
    const remainder = checkExists(this.values.get(this.remainder));
    const remainderPlated = checkExists(remainder.value) as PlatedMaterialValue;

    const [mixesType, mixesValue] = updateTable(mixesTable, platedMaterials);
    let allMade;
    try {
      allMade = makeMixedMaterials(
        platedMaterials,
        mixesValue,
        context.lab.plate(plateId),
      );
    } catch (e: unknown) {
      mixes.reject(e);
      return;
    }

    const constraints = await this.gatherConstraints(
      {
        kind: 'array',
        elements: allMade,
      },
      context,
    );
    const arrayConstraints = constraints.array ?? [];

    const request: number[] = [];
    let totalNl = 0;
    for (let i = 0; i < arrayConstraints.length; ++i) {
      const source = arrayConstraints[i] ?? {};
      const need = source.volume?.numberNl ?? 0;
      const making = need + (need > 0 ? REMAINDER_WELL_VOLUME_NL : 0);
      request.push(making);
      totalNl = Math.max(totalNl, making);
    }

    if (totalNl === 0) {
      mixes.resolve({
        type: mixesType,
        value: {
          expected: mixesValue,
          reason: 'May have updated table',
        },
      });
      return;
    }

    const minimumTotalNl = totalNl;
    const made = allMade.filter((_, i) => request[i] > 0);
    const mixingPlate = context.lab.plate(plateId);
    const step = MINIMUM_STEP_VOLUME_NL;
    let result;
    while (minimumTotalNl > 0 && totalNl < 100 * minimumTotalNl) {
      try {
        result = getPlan(made, totalNl, mixingPlate);
        break;
      } catch (e: unknown) {
        if (e instanceof InvalidMixError) {
          mixes.reject(e);
          return;
        } else if (e instanceof ImpossibleAmountsError) {
          result = e;
          totalNl = Math.floor((totalNl + step) / step) * step;
        } else {
          throw e;
        }
      }
    }

    if (result instanceof ImpossibleAmountsError) {
      mixes.reject(result);
      return;
    }

    const plan = checkExists(result);
    const maybePremix = getPremix(
      platedMaterials,
      made,
      totalNl,
      context,
      plan.sortedByVolume,
      plan.commonRemainderNl,
      mixingPlate,
    );
    const {actions, inspection, used} = makeReactions(
      platedMaterials,
      made,
      totalNl,
      remainderPlated,
      context,
      mixingPlate,
      plan.sortedByVolume,
      maybePremix,
    );
    context.changes.addNodeNote(
      this.id.node,
      'Pipette plan',
      createPipettePlanNote(platedMaterials, made, inspection),
    );

    const promises = [];
    for (const {source, value, resolve, reject} of this.values.values()) {
      if (source === this.mixes) {
        // With mix array nodes, there's a circular loop like
        // varying --> mix array flattener
        //         \-----------------------\+-> mixing transformer
        // so if we await here then we never resolve. Better to push the promise and await on all
        // promises.
        promises.push(
          mixes.resolve({
            type: mixesType,
            value: {
              expected: mixesValue,
              reason: 'May have updated table',
            },
          }),
        );
      } else if (
        this.materials.indexOf(source as any) >= 0 ||
        source === this.remainder
      ) {
        promises.push(
          resolve(
            walkMaterials(value, reject, v => {
              if (!v) {
                return {};
              }

              return {volume: used.get(v)};
            }),
          ),
        );
      } else {
        promises.push(resolve({}));
      }
    }
    await Promise.all(promises);

    const actor = context.dispatcher.actor(this);
    for (const action of actions) {
      actor.pipette(...action);
    }
  }
}

function getPlan(
  outputs: PlatedMaterialValue[],
  totalNl: number,
  mixingPlate: PlateState,
): {
  commonRemainderNl: number;
  sortedByVolume: Array<{
    dependants: PlatedMaterialValue[];
    material: MaterialValue;
    maxNumberNl: number;
  }>;
} {
  const impossible = new Set<string>();
  const using = new DefaultMap<
    MaterialId,
    {
      dependants: PlatedMaterialValue[];
      materials: MaterialValue[];
      maxNumberNl: number;
    }
  >(() => ({dependants: [], materials: [], maxNumberNl: 0}));
  const remainders = [];
  for (const plated of outputs) {
    let madeTotalNl = 0;
    for (const c of plated.material.contents) {
      let neededNl;
      if (c.amount.kind === 'concentration') {
        const initial = c.contents[0].amount;
        if (initial.kind !== 'concentration') {
          throw new Error('Expected the initial to be a concentration as well');
        }
        // TODO(april): is using dependants = 1 here safe?
        neededNl = concentrationMix(
          initial,
          c.amount,
          /* dependants= */ 1,
          totalNl,
          mixingPlate.maxWellVolumeNl,
        ).outputNl;
      } else if (c.amount.kind === 'volume') {
        neededNl = volumeMix(c.amount);
      } else {
        throw checkExhaustive(c.amount);
      }

      if (neededNl === 0) {
        continue;
      }

      madeTotalNl += neededNl;
      const u = using.get(c.id);
      u.dependants.push(plated);
      u.materials.push(c);
      u.maxNumberNl = Math.max(u.maxNumberNl, neededNl);
    }

    const remainderNl = totalNl - madeTotalNl;
    remainders.push(remainderNl);
    if (approxEqual(totalNl, madeTotalNl, /* epsilon= */ 1 /* nl */)) {
      // yay!
    } else if (
      totalNl > madeTotalNl &&
      remainderNl >= MINIMUM_PIPETTE_VOLUME_NL
    ) {
      // If we can just add remainder, life is great
    } else {
      impossible.add(plated.material.label);
    }
  }

  if (impossible.size > 0) {
    throw new ImpossibleAmountsError(
      `Materials ${[...impossible].join(', ')} have impossible amounts`,
    );
  }

  const sortedByVolume = [...using].sort(
    ([, v1], [, v2]) => v2.maxNumberNl - v1.maxNumberNl,
  );

  let commonRemainderNl = Math.min(totalNl, ...remainders);
  if (commonRemainderNl < MINIMUM_PIPETTE_VOLUME_NL) {
    commonRemainderNl = 0;
  }
  for (const remainder of remainders) {
    const uncommonRemainderNl = remainder - commonRemainderNl;
    if (
      uncommonRemainderNl > 0 &&
      uncommonRemainderNl < MINIMUM_PIPETTE_VOLUME_NL
    ) {
      commonRemainderNl = 0;
    }
  }

  return {
    commonRemainderNl,
    sortedByVolume: sortedByVolume.map(
      ([, {dependants, materials, maxNumberNl}]) => ({
        dependants,
        material: checkExists(materials[0]),
        maxNumberNl,
      }),
    ),
  };
}

function getPremix(
  inputs: PlatedMaterialValue[],
  outputs: PlatedMaterialValue[],
  totalNl: number,
  context: PipeContext,
  sortedByVolume: Array<{
    dependants: PlatedMaterialValue[];
    material: MaterialValue;
    maxNumberNl: number;
  }>,
  commonRemainderNl: number,
  premixPlate: PlateState,
):
  | {
      commonRemainderNl: number;
      neededNl: number;
      well: WellState;
    }
  | undefined {
  const premixMaterials = new Map<
    MaterialId,
    {
      dependants: PlatedMaterialValue[];
      material: MaterialValue;
    }
  >();
  let premixNl = 0;
  for (const {dependants, material, maxNumberNl} of sortedByVolume) {
    if (dependants.length === outputs.length) {
      premixMaterials.set(material.id, {dependants, material});
      premixNl += maxNumberNl;
    }
  }
  // Ensure we can mix both the remainder and the premix in one well
  // TODO(april): what do we do if the premix on its own exceeds this volume? How do we even know
  // this is the max volume? Shouldn't this be done at a different level?
  if (
    outputs.length * (premixNl + commonRemainderNl) >
    premixPlate.maxWellVolumeNl
  ) {
    commonRemainderNl = 0;
  }

  if (premixMaterials.size === 1) {
    const {dependants, material} = getOnlyElement(premixMaterials.values());
    const initial = material.contents[0];
    const source = getOnlyElement(
      inputs.filter(p => p.material.id === initial.id),
    );
    if (material.amount.kind === 'concentration') {
      if (initial.amount.kind !== 'concentration') {
        throw new Error('Expected the initial to be a concentration as well');
      }
      const {dilutionCount, outputNl} = concentrationMix(
        initial.amount,
        material.amount,
        dependants.length,
        dependants.length * totalNl,
        premixPlate.maxWellVolumeNl,
      );
      if (dilutionCount === 0) {
        return {
          commonRemainderNl: 0,
          neededNl: outputNl / dependants.length,
          well: context.lab.plate(source.plate).well(source.well),
        };
      }
    } else if (material.amount.kind === 'volume') {
      if (volumeMix(material.amount) >= MINIMUM_PIPETTE_VOLUME_NL) {
        return {
          commonRemainderNl: 0,
          neededNl: volumeMix(material.amount),
          well: context.lab.plate(source.plate).well(source.well),
        };
      }
    } else {
      throw checkExhaustive(material.amount);
    }
  }

  if (premixMaterials.size >= 1) {
    checkState(
      outputs.length * (premixNl + commonRemainderNl) <=
        premixPlate.maxWellVolumeNl,
      `Premix volume ${(outputs.length * (premixNl + commonRemainderNl)) / 1000}ul will exceed` +
        `max well volume ${premixPlate.maxWellVolumeNl / 1000}ul`,
    );
    return {
      commonRemainderNl,
      neededNl: premixNl,
      well: premixPlate.reserveWell(),
    };
  }

  return undefined;
}

function makeReactions(
  inputs: PlatedMaterialValue[],
  outputs: PlatedMaterialValue[],
  totalNl: number,
  buffer: PlatedMaterialValue,
  context: PipeContext,
  mixingPlate: PlateState,
  sortedByVolume: Array<{
    dependants: PlatedMaterialValue[];
    material: MaterialValue;
    maxNumberNl: number;
  }>,
  maybePremix:
    | {
        commonRemainderNl: number;
        neededNl: number;
        well: WellState;
      }
    | undefined,
): {
  actions: Array<Parameters<ActionsBuffer['pipette']>>;
  inspection: DefaultMap<string, Map<string, number>>;
  used: DefaultMap<PlatedMaterialValue, VolumeValue>;
} {
  const bufferWell = context.lab.plate(buffer.plate).well(buffer.well);
  const currentNl = new DefaultMap<PlatedMaterialValue, {numberNl: 0}>(() => ({
    numberNl: 0,
  }));
  const inspection = new DefaultMap<string, Map<string, number>>(
    () => new Map(),
  );
  const used = new DefaultMap<PlatedMaterialValue, VolumeValue>(() => ({
    kind: 'volume',
    numberNl: 0,
    preferredUnit: 'ul',
  }));

  const premixIsIntermediate =
    inputs.filter(
      i =>
        i.plate === maybePremix?.well.plate && i.well === maybePremix.well.id,
    ).length === 0;
  let scalePremix;
  if (maybePremix) {
    const premixNl =
      outputs.length * (maybePremix.neededNl + maybePremix.commonRemainderNl);
    // Ensure we scale everything so that we have REMAINDER_WELL_VOLUME_NL in the intermediate
    // premix.
    scalePremix = premixIsIntermediate
      ? (premixNl + REMAINDER_WELL_VOLUME_NL) / premixNl
      : 1;
  } else {
    scalePremix = 1;
  }

  let premixNl = 0;
  const bufferActions: Array<Parameters<ActionsBuffer['pipette']>> = [];
  const premixActions: Array<Parameters<ActionsBuffer['pipette']>> = [];
  const mixingActions: Array<Parameters<ActionsBuffer['pipette']>> = [];
  for (const {dependants, material} of sortedByVolume) {
    // We assume that the material looks like:
    //         aggregate
    //             |
    // dilution or volume reduction
    //             |
    //   all the real contents
    //
    // So we go two levels deep to get the real material ID, and then use that to find all
    // sources. All sources should have the same amounts if our material ID is good.
    const original = material.contents[0];
    const from = checkExists(
      inputs.filter(i => i.material.id === original.id)[0],
    );
    const inPremix = dependants.length === outputs.length;
    const actions = inPremix ? premixActions : mixingActions;

    // In the case where the premix is the source well, we don't need to do anything
    if (maybePremix && inPremix && !premixIsIntermediate) {
      used.get(from).numberNl += dependants.length * maybePremix.neededNl;
      continue;
    }
    const scale = inPremix ? scalePremix : 1;

    let fromLabel;
    let fromNeededNl;
    let fromWell;
    if (material.amount.kind === 'concentration') {
      const initial = material.contents[0].amount;
      if (initial.kind !== 'concentration') {
        throw new Error('Expected the initial to be a concentration as well');
      }

      const consumers = inPremix ? 1 : dependants.length;
      const {absoluteDilution, dilutionCount, inputNl, outputNl} =
        concentrationMix(
          initial,
          material.amount,
          consumers,
          dependants.length * totalNl,
          mixingPlate.maxWellVolumeNl,
        );
      const dilutingRemainderNl =
        inputNl * (Math.pow(absoluteDilution, 1 / dilutionCount) - 1);
      // Ensure at each dilution well we also have REMAINDER_WELL_VOLUME_NL left over
      const scaleSerials =
        dilutionCount > 0
          ? (inputNl + dilutingRemainderNl + REMAINDER_WELL_VOLUME_NL) /
            (inputNl + dilutingRemainderNl)
          : 1;
      let last = context.lab.plate(from.plate).well(from.well);
      let lastLabel = original.label;
      for (let i = 0; i < dilutionCount; ++i) {
        const next = mixingPlate.reserveWell();
        // When not in premix, each output has its own dilution well, so include
        // the dependant label(s) to make the inspection table label unique
        const dependantSuffix =
          inPremix || dependants.length === 0
            ? ''
            : ` (for ${dependants.map(d => d.material.label).join(', ')})`;
        const label = `${original.label} dilution ${i}${dependantSuffix}`;
        bufferActions.push([
          bufferWell,
          next,
          {mix: false, volumeNl: scaleSerials * dilutingRemainderNl},
        ]);
        used.get(buffer).numberNl += scaleSerials * dilutingRemainderNl;
        inspection
          .get(label)
          .set(buffer.material.label, scaleSerials * dilutingRemainderNl);
        actions.push([
          last,
          next,
          {mix: true, volumeNl: scaleSerials * inputNl},
        ]);
        inspection.get(label).set(lastLabel, scaleSerials * inputNl);
        last = next;
        lastLabel = label;
      }
      fromLabel = lastLabel;
      fromNeededNl = scale * outputNl;
      used.get(from).numberNl +=
        (dilutionCount > 0 ? scaleSerials : scale) * inputNl;
      fromWell = last;
    } else if (material.amount.kind === 'volume') {
      fromLabel = original.label;
      fromNeededNl = scale * dependants.length * volumeMix(material.amount);
      used.get(from).numberNl += fromNeededNl;
      fromWell = context.lab.plate(from.plate).well(from.well);
    } else {
      throw checkExhaustive(material.amount);
    }

    if (inPremix) {
      const premix = checkExists(maybePremix);
      actions.push([
        fromWell,
        premix.well,
        {mix: true, volumeNl: fromNeededNl},
      ]);
      inspection.get(PREMIX_LABEL).set(fromLabel, fromNeededNl);
      premixNl += fromNeededNl;
    } else {
      const perOutputNl = fromNeededNl / dependants.length;
      for (const dependant of dependants) {
        const toWell = context.lab.plate(dependant.plate).well(dependant.well);
        actions.push([fromWell, toWell, {mix: true, volumeNl: perOutputNl}]);
        currentNl.get(dependant).numberNl += perOutputNl;
        inspection.get(dependant.material.label).set(fromLabel, perOutputNl);
      }
    }
  }

  if (maybePremix && premixIsIntermediate) {
    const bufferNl =
      scalePremix *
        outputs.length *
        (maybePremix.neededNl + maybePremix.commonRemainderNl) -
      premixNl;
    bufferActions.push([
      bufferWell,
      maybePremix.well,
      {mix: false, volumeNl: bufferNl},
    ]);
    used.get(buffer).numberNl += bufferNl;
    inspection.get(PREMIX_LABEL).set(buffer.material.label, bufferNl);
  }

  for (const output of outputs) {
    const outputWell = context.lab.plate(output.plate).well(output.well);
    if (maybePremix) {
      const volumeNl = maybePremix.commonRemainderNl + maybePremix.neededNl;
      premixActions.push([maybePremix.well, outputWell, {mix: true, volumeNl}]);
      currentNl.get(output).numberNl += volumeNl;
      if (premixIsIntermediate) {
        inspection.get(output.material.label).set(PREMIX_LABEL, volumeNl);
      } else {
        const sole = getOnlyElement(
          inputs.filter(
            i =>
              i.plate === maybePremix.well.plate &&
              i.well === maybePremix.well.id,
          ),
        );
        inspection
          .get(output.material.label)
          .set(sole.material.label, volumeNl);
      }
    }

    const remainderNl = totalNl - currentNl.get(output).numberNl;
    // Don't do anything if this is below 1nl, assume it's rounding error.
    if (remainderNl >= 1) {
      bufferActions.push([
        bufferWell,
        outputWell,
        {mix: false, volumeNl: remainderNl},
      ]);
      used.get(buffer).numberNl += remainderNl;
      inspection
        .get(output.material.label)
        .set(buffer.material.label, remainderNl);
    }
  }

  return {
    actions: [...bufferActions, ...premixActions, ...mixingActions],
    inspection,
    used,
  };
}

function concentrationMix(
  from: ConcentrationValue,
  to: ConcentrationValue,
  partitions: number,
  totalNl: number,
  maxWellVolumeNl: number,
): {
  absoluteDilution: number;
  dilutionCount: number;
  inputNl: number;
  outputNl: number;
} {
  // concentrationMixDilute only works up to the maximum well volume, but once we have that we can
  // scale up the whole thing.
  const nl = Math.min(maxWellVolumeNl - REMAINDER_WELL_VOLUME_NL, totalNl);
  const inverseFraction = totalNl / nl;
  const {absoluteDilution, dilutionCount, inputNl, outputNl} =
    concentrationMixDilute(from, to, partitions, nl, maxWellVolumeNl);
  if (dilutionCount < 0) {
    throw new ImpossibleAmountsError(
      `Making a ${to.number}${to.unit} solution from ${from.number}${from.unit} is impossible`,
    );
  }
  return {
    absoluteDilution,
    dilutionCount,
    inputNl: inputNl * inverseFraction,
    outputNl: outputNl * inverseFraction,
  };
}

function concentrationMixDilute(
  from: ConcentrationValue,
  to: ConcentrationValue,
  partitions: number,
  totalNl: number,
  maxWellVolumeNl: number,
): {
  absoluteDilution: number;
  dilutionCount: number;
  inputNl: number;
  outputNl: number;
} {
  const harmonized = harmonizeUnits(from.unit, to.unit);
  if (!harmonized) {
    throw new InvalidMixError(
      `Cannot dilute from ${formatConcentration(from)} to ${formatConcentration(to)}`,
    );
  }
  const harmonyScale = harmonized[1] / harmonized[0];
  checkArgument(
    totalNl <= maxWellVolumeNl - REMAINDER_WELL_VOLUME_NL,
    'Not enough space in wells to dilute',
  );

  const maximumDilution = MINIMUM_PIPETTE_VOLUME_NL / maxWellVolumeNl;
  const needNl = ((totalNl * to.number) / from.number) * harmonyScale;

  // How much dilution is needed to allow for the smallest input to be added?
  let absoluteDilution = MINIMUM_PIPETTE_VOLUME_NL / needNl;
  // How many times are we going to have to dilute given we can only dilute so much?
  let dilutionCount = Math.ceil(
    -Math.log(absoluteDilution) / Math.log(maximumDilution),
  );

  // If dilutionCount === 0 then we're doing (potentially) multiple transfers out of the source well
  // directly into the reactions. In this case, we need to ensure that we can do `partitions`
  // transfers. When dilutionCount > 0 this isn't a problem because we only do one transfer out of
  // the source well.
  if (dilutionCount === 0) {
    absoluteDilution = Math.max(
      1,
      (MINIMUM_PIPETTE_VOLUME_NL * partitions) / needNl,
    );
    dilutionCount = Math.ceil(
      -Math.log(absoluteDilution) / Math.log(maximumDilution),
    );
  }

  // How much are we diluting every time?
  const serialDilution = 1 / Math.pow(absoluteDilution, 1 / dilutionCount);
  const lastDilution = from.number * Math.pow(serialDilution, dilutionCount);
  // How much liquid are we adding to the final solution?
  const inputNl = ((totalNl * to.number) / lastDilution) * harmonyScale;
  const remainderNl =
    inputNl * (Math.pow(absoluteDilution, 1 / dilutionCount) - 1);
  // Find the dilution factor that enables us to output in partitions
  const outputFactor = Math.max(
    1,
    (MINIMUM_PIPETTE_VOLUME_NL * partitions) / inputNl,
  );
  // Find the dilution factor that enables us to dilute using remainder, assuming it's bigger than
  // the current absolute
  const remainderFactor = Math.max(
    absoluteDilution,
    remainderNl > 0
      ? (MINIMUM_PIPETTE_VOLUME_NL * partitions) / remainderNl
      : 1,
  );
  // Multiply to find our new dilution factor
  const adjustFactor = outputFactor * remainderFactor;
  const remainderCount = Math.ceil(
    -Math.log(adjustFactor) / Math.log(maximumDilution),
  );
  const remainderSerialDilution =
    1 / Math.pow(adjustFactor, 1 / remainderCount);
  const remainderDilution =
    from.number * Math.pow(remainderSerialDilution, remainderCount);
  const remainderInputNl =
    ((totalNl * to.number) / remainderDilution) * harmonyScale;
  return {
    absoluteDilution: adjustFactor,
    dilutionCount: remainderCount,
    inputNl,
    outputNl: remainderInputNl,
  };
}

function volumeMix(volume: VolumeValue): number {
  const usedNl = volume.numberNl;
  if (usedNl < MINIMUM_PIPETTE_VOLUME_NL) {
    throw new Error(`${usedNl}nl is below the minimum pipetting volume`);
  }
  return usedNl;
}

export function createPipettePlanNote(
  inputs: PlatedMaterialValue[],
  outputs: PlatedMaterialValue[],
  inspection: DefaultMap<string, Map<string, number>>,
): TableValue {
  const keys = new Set<string>();
  // Order the columns as:
  // * (maybe) premix
  // * inputs
  // * everything else
  if (inspection.has(PREMIX_LABEL)) {
    keys.add(PREMIX_LABEL);
  }
  for (const label of inputs.map(i => i.material.label).sort()) {
    keys.add(label);
  }
  for (const row of inspection.values()) {
    [...row.keys()].sort().forEach(l => {
      keys.add(l);
    });
  }

  // Order the rows as:
  // * outputs
  // * (maybe) premix
  // * everything else
  const reorder = new Map<string, Map<string, number>>();
  for (const label of outputs.map(i => i.material.label).sort()) {
    const row = inspection.get(label);
    if (row) {
      reorder.set(label, row);
    }
  }
  // We can't use .get here because it will create the row even if it doesn't exist
  if (inspection.has(PREMIX_LABEL)) {
    reorder.set(PREMIX_LABEL, checkExists(inspection.get(PREMIX_LABEL)));
  }
  for (const [label, row] of inspection) {
    if (!reorder.has(label)) {
      reorder.set(label, row);
    }
  }

  const table: TableValue = {
    kind: 'table',
    values: new Map([LABEL_COLUMN_ID, ...keys].map(k => [k, []])),
  };
  const values = table.values;
  for (const [label, row] of reorder) {
    checkExists(values.get(LABEL_COLUMN_ID)).push({
      kind: 'string',
      value: label,
    });
    for (const key of keys) {
      const column = checkExists(values.get(key));
      const nl = row.get(key) ?? 0;
      column.push({kind: 'volume', numberNl: nl, preferredUnit: 'ul'});
    }
  }
  return table;
}

function makeMixedMaterials(
  plated: PlatedMaterialValue[],
  mixes: TableValue,
  plate: PlateState,
): PlatedMaterialValue[] {
  const byLabel = new Map(plated.map(p => [p.material.label, p]));
  const made: MaterialValue[] = [];
  for (let i = 0; i < getFirstElement(mixes.values.values()).length; ++i) {
    const label = (
      checkExists(mixes.values.get(LABEL_COLUMN_ID))[i] as StringValue
    ).value;
    const contents: MaterialValue[] = [];
    for (const [columnLabel, columnValues] of mixes.values) {
      if (columnLabel === LABEL_COLUMN_ID) {
        continue;
      }

      const plated = checkExists(byLabel.get(columnLabel));
      const amount = checkExists(columnValues[i]);
      if (amount.kind === 'concentration') {
        if (amount.number === 0) {
          continue;
        }
      } else if (amount.kind === 'volume') {
        if (amount.numberNl === 0) {
          continue;
        }
      } else {
        throw new InvalidMixError(
          `Unexpected amount type: ${JSON.stringify(amount)}`,
        );
      }

      contents.push(reduceMaterial(plated.material, amount));
    }

    const material: MaterialValue = {
      kind: 'material',
      id: 'uncalculated' as MaterialId,
      label,
      amount: {kind: 'concentration', number: 1, unit: 'x'},
      contents,
      tags: {treatment: MIXED_TREATMENT},
    };
    material.id = hashMaterial(material);
    made.push(material);
  }

  return made.map(m => ({
    kind: 'plated_material',
    material: m,
    plate: plate.id,
    well: plate.reserveWell().id,
  }));
}

function updateTable(
  existing: TableValue,
  platedMaterials: PlatedMaterialValue[],
): [TableType, TableValue] {
  const materials: MaterialValue[] = [];
  const initialAmounts: Array<ConcentrationValue | VolumeValue> = [];
  for (const plated of platedMaterials) {
    materials.push(plated.material);
    initialAmounts.push(plated.material.amount);
  }

  const type: TableType = {
    kind: 'table',
    columnLabels: ['Label', ...materials.map(m => m.label)],
    columnTypes: [
      {kind: 'string'},
      ...initialAmounts.map(a => {
        if (a.kind === 'concentration') {
          return {kind: 'concentration'} as const;
        } else if (a.kind === 'volume') {
          return {
            kind: 'volume',
            minimumNl: 0,
            maximumNl: undefined,
          } as const;
        } else {
          throw checkExhaustive(a);
        }
      }),
    ],
    defaults: [{kind: 'string', value: '(unnamed)'}, ...initialAmounts],
    rowCountFixed: false,
  };

  // We intentionally use the labels, rather than the ID, as the column ID so it's resistant to
  // upstream concentration changes.
  const columns = type.columnLabels;
  // TODO(april): if we change the first element will this clear the table?
  const rows = existing.values?.get(columns[0])?.length ?? 0;

  let willChange = false;
  for (const column of columns) {
    const old = existing?.values?.get(column);
    if (!old || old.length !== rows) {
      willChange = true;
      break;
    }
  }
  if (!willChange && existing?.values.size === columns.length) {
    return [type, existing];
  }

  const table: TableValue = {
    kind: 'table',
    values: new Map(),
  };

  const factories = new Map<string, (i: number) => DataValue>([
    [LABEL_COLUMN_ID, i => ({kind: 'string', value: asAlphaId(i)})],
    ...materials.map((m, i) => [m.label, () => initialAmounts[i]] as const),
  ]);
  for (const column of columns) {
    const old = existing.values?.get(column);
    let now;
    if (old) {
      now = old.slice(0, rows);
    } else {
      now = Array(rows);
    }
    table.values.set(column, now);

    const factory = checkExists(factories.get(column));
    for (let i = 0; i < rows; ++i) {
      // This is *super* dissatisfying because we effectively swallow bad mix changes. It'd be
      // better to put everything at 1x so we immediately get impossible amount errors and the user
      // has to fix it. But to get an impossible amount error we have to reject the promise, and to
      // update the table we have to resolve the promise, so???
      //
      // Maybe the answer is to resolve and add a thing to constraints that actually marks it as
      // rejected. But that seems weird.
      now[i] ??= toZero(factory(i));
    }
  }

  return [type, table];
}

function asAlphaId(i: number): string {
  const a = [];
  const base = 'a'.charCodeAt(0);
  do {
    a.push((i % 26) + base);
    i = Math.floor(i / 26);
  } while (i > 0);
  a.reverse();
  return String.fromCharCode(...a);
}

function formatConcentration(value: ConcentrationValue): string {
  return `${value.number}${value.unit}`;
}

export function reduceMaterial(
  material: MaterialValue,
  to: ConcentrationValue | VolumeValue,
): MaterialValue {
  const diluted: MaterialValue = {
    kind: 'material',
    id: 'uncalculated' as unknown as MaterialId,
    label: material.label,
    amount: to,
    contents: [material],
    tags: {treatment: REDUCED_TREATMENT},
  };
  diluted.id = hashMaterial(diluted);
  return diluted;
}

export function toZero(v: DataValue): DataValue {
  if (v.kind === 'concentration') {
    return {...v, number: 0};
  } else if (v.kind === 'volume') {
    return {...v, numberNl: 0};
  } else {
    return v;
  }
}

class ImpossibleAmountsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
  }
}

export class InvalidMixError extends Error {
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
  }
}
